import React from 'react';
import { createPortal } from 'react-dom';
import type { WiseguyAlert } from '../types/wiseguy';
import {
  formatConditionCombined,
  formatIv,
  formatIvChgPct,
  formatLast,
  formatOi,
  formatQty,
  formatSide,
} from './wiseguy/alertFormat';

interface WiseguyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  alert: WiseguyAlert | null;
}

/** Muted em dash for an absent value, matching the tables. */
const DASH = <span className="text-gray-600">—</span>;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Expiry as `Sep 18, 2026`.
 *
 * ⚠️ Split by hand rather than `new Date(expiry)`. Parsing a bare `YYYY-MM-DD`
 * gives UTC midnight, which formats as the day before anywhere west of UTC —
 * every expiry in the US would read one day early.
 */
function fmtExpiry(expiry: string | null | undefined): string | null {
  if (!expiry) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(expiry);
  if (!m) return expiry;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return expiry;
  return `${month} ${Number(m[3])}, ${m[1]}`;
}

/**
 * The received timestamp, split into `14:46:03` and `Sep 8, 2026`.
 *
 * ⚠️ Pinned to `America/New_York`, not the browser's zone, because the label
 * beside it says ET and the tape's whole session model is ET. A trader in
 * London would otherwise read a London clock under an ET caption.
 */
function fmtReceived(iso: string | null | undefined): { time: string; date: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const opts = { timeZone: 'America/New_York' } as const;
  return {
    time: d.toLocaleTimeString('en-US', { ...opts, hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    date: d.toLocaleDateString('en-US', { ...opts, month: 'short', day: 'numeric', year: 'numeric' }),
  };
}

/** `MultiSweep` → `MULTI-SWEEP`. Presentation only; the token is unchanged. */
function fmtAlertType(alertType: string | null | undefined): string | null {
  if (!alertType) return null;
  return alertType.replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/[\s_]+/g, '-').toUpperCase();
}

/**
 * The alert's `multiplier`, passed straight through as `1.2x`.
 *
 * ⚠️ Not derived here. The producer computes this number and the modal only
 * prints it — same field, same one-decimal `x` format as the tape's Mult
 * column, so the two cannot disagree.
 */
function fmtMultiplier(multiplier: string | null | undefined): string | null {
  if (multiplier == null || multiplier === '') return null;
  const n = parseFloat(multiplier);
  if (Number.isNaN(n)) return null;
  return `${n.toFixed(1)}x`;
}

/** One label/value pair. Label left, value right, as in the mock. */
function Field({
  label,
  children,
  valueClass = 'text-white',
}: {
  label: string;
  children: React.ReactNode;
  valueClass?: string;
}): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
      <span className={`text-sm font-numeric font-semibold text-right ${valueClass}`}>{children}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="text-[10px] uppercase tracking-[1.5px] text-accent-yellow font-semibold mb-2.5">
      {children}
    </div>
  );
}

export const WiseguyDetailModal: React.FC<WiseguyDetailModalProps> = ({
  isOpen,
  onClose,
  alert,
}) => {
  if (!isOpen || !alert) return null;

  /**
   * Direction, from `contract_type` — the same field as the C/P badge, the
   * symbol dot and the optional coloured-symbol mode on the tape, so the modal
   * cannot contradict the row it was opened from.
   *
   * ⚠️ This is the INSTRUMENT's direction, not the position's: a call seller
   * is bearish but reads blue here. See the `formatSide` note in
   * `wiseguy/alertFormat.ts`.
   */
  const isCall = alert.contract_type === 'Call';
  const dirText = isCall ? 'text-bull-blue' : 'text-bear-red';
  const dirBg = isCall ? 'bg-bull-blue' : 'bg-bear-red';

  const biasClass =
    alert.bias === 'Bullish'
      ? 'bg-bull-blue/15 text-bull-blue'
      : alert.bias === 'Bearish'
        ? 'bg-bear-red/15 text-bear-red'
        : 'bg-gray-600/20 text-gray-400';

  const expiry = fmtExpiry(alert.expiry);
  const dte = alert.time_to_expiry_days;
  const received = fmtReceived(alert.received_on);
  const condition = formatConditionCombined(alert);
  const multiplier = fmtMultiplier(alert.multiplier);
  const alertType = fmtAlertType(alert.alert_type);
  const side = formatSide(alert);

  return createPortal(
    /* ⚠️ Portalled to `document.body`. It was rendered inline, which the
       widget guide warns against: react-grid-layout puts a CSS `transform` on
       every widget, and a transformed ancestor makes `position: fixed`
       resolve against the widget instead of the viewport. The sibling
       settings modal already does this. */
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-surface border border-dark-border rounded-xl w-full max-w-md overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Direction hairline */}
        <div className={`h-0.5 w-full ${dirBg}`} />

        {/* Header — symbol, asset class, bias */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div className="flex items-center gap-2.5">
            <span className="text-2xl font-bold text-white tracking-tight">{alert.symbol}</span>
            {alert.asset_class && (
              <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                {alert.asset_class}
              </span>
            )}
            {alert.bias && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${biasClass}`}>
                {alert.bias}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-7 h-7 flex items-center justify-center rounded-md bg-dark-surface-light text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contract + premium */}
        <div className="flex items-end justify-between px-5 pb-4">
          <div>
            <div className="text-2xl font-numeric font-bold text-white leading-none">
              {alert.strike}{' '}
              <span className={dirText}>{alert.contract_type?.toUpperCase()}</span>
            </div>
            {/* Labelled because the footer carries a second date (received),
                and an unlabelled one under the contract reads as the trade
                date just as easily as the expiry. "DTE" only disambiguates it
                for a reader who already expands the acronym. */}
            <div className="mt-1.5 text-[13px] text-gray-400 font-numeric">
              {/* ⚠️ Not `text-gray-600` (#61615E). That sits a few points off
                  the panel it is printed on and the label was barely visible;
                  gray-500 is the dimmest step that still reads on
                  `dark-surface`, and the date itself takes gray-400 — the
                  palette's own "timestamps, secondary" tone. */}
              <span className="uppercase tracking-[1px] text-gray-500 font-sans font-semibold mr-1.5">
                Exp
              </span>
              {expiry ?? DASH}
              {/* Neutral on purpose — no colour ramp for near-dated contracts.
                  That would be a threshold nobody has set, and 0DTE red beside
                  a blue CALL reads as a direction. */}
              {dte !== undefined && (
                <span className="ml-2 px-1.5 py-0.5 rounded border border-gray-700 bg-dark-surface-light text-gray-300 text-[10px] font-semibold tracking-wide align-middle">
                  {dte} DTE
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[1.5px] text-gray-500 font-semibold">Premium</div>
            <div className="text-2xl font-numeric font-bold text-accent-yellow leading-none mt-1">
              {alert.bet_display || DASH}
            </div>
          </div>
        </div>

        {/* The print */}
        <div className="px-5 py-4 border-t border-dark-border bg-dark-bg/40">
          <SectionLabel>The print</SectionLabel>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Field label="Qty">{formatQty(alert.qty)}</Field>
            <Field label="Last">
              {alert.last_price ? `$${formatLast(alert.last_price)}` : DASH}
            </Field>
            <Field label="Type">{alertType ?? DASH}</Field>
            {/* ⚠️ Left bone, not coloured. The mock shows this blue, but
                `aggressor_class` is a fill location against the NBBO — tinting
                "Above Ask" bullish and "Below Bid" bearish would assert the
                buy/sell inference the C/P note in `alertFormat.ts`
                deliberately refuses to make. */}
            <Field label="Side">{side === '-' ? DASH : side.toUpperCase()}</Field>
            <Field label="Spot at print">
              {alert.reference_price ? `$${alert.reference_price}` : DASH}
            </Field>
            {/* ⚠️ Two decimals, so 0.38 reads as a delta. The tape's Delta
                column shows the same field as points (38) via `formatDelta`. */}
            <Field label="Delta">
              {alert.delta == null || Number.isNaN(alert.delta) ? DASH : alert.delta.toFixed(2)}
            </Field>
          </div>
        </div>

        {/* Market context */}
        <div className="px-5 py-4 border-t border-dark-border">
          <SectionLabel>Market context</SectionLabel>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {/* ⚠️ `formatOi`, not `formatQty` — the tape renders both of these
                fields abbreviated (33.8K), and a detail view that spelled them
                out in full would not visibly match the row it was opened
                from. `qty` above keeps `formatQty`: the table does too. */}
            <Field label="Volume">{formatOi(alert.volume)}</Field>
            <Field label="Open interest">{formatOi(alert.open_interest)}</Field>
            <Field label="Multiplier" valueClass="text-accent-yellow">
              {multiplier ?? DASH}
            </Field>
            <Field label="IV">
              {alert.iv == null ? (
                DASH
              ) : (
                <>
                  {formatIv(alert.iv)}
                  {alert.iv_chg_pct != null && (
                    <span
                      className={`ml-1.5 text-xs ${alert.iv_chg_pct >= 0 ? 'text-bull-blue' : 'text-bear-red'}`}
                    >
                      {formatIvChgPct(alert.iv_chg_pct)}
                    </span>
                  )}
                </>
              )}
            </Field>
            <div className="col-span-2">
              <Field label="Condition">
                {condition === '-' ? DASH : condition.toUpperCase()}
              </Field>
            </div>
          </div>
        </div>

        {/* Commentary (only shown if present) */}
        {alert.comment && (
          <div className="px-5 py-4 border-t border-dark-border">
            <SectionLabel>Commentary</SectionLabel>
            <p className="text-sm text-gray-300 leading-relaxed">{alert.comment}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-2.5 border-t border-dark-border bg-dark-bg/40">
          <span className="text-[11px] text-gray-500 font-numeric">
            {received ? `Received ${received.time} ET · ${received.date}` : 'Received —'}
          </span>
          <span className="text-[10px] text-gray-600 flex-shrink-0">
            powered by{' '}
            <a
              href="https://fznanalytics.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold tracking-[0.5px] text-accent-yellow/70 hover:text-accent-yellow hover:underline transition-colors"
            >
              FZN Analytics
            </a>
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};
